// Mock data for Uptown Physiotherapy Clinic
import {
  Activity,
  Bike,
  HandHeart,
  Sparkles,
  Waves,
  Dumbbell,
  Brain,
  Baby,
  Shield,
  Wind,
  Bone,
  Zap,
  Monitor,
  Flame,
  HeartPulse,
} from 'lucide-react';

export const services = [
  {
    slug: 'physiotherapy',
    name: 'Physiotherapy',
    icon: HandHeart,
    tagline: 'Restore movement. Rebuild confidence.',
    short:
      'Personalized care for pain, mobility, strength, balance, and recovery after injury or surgery.',
    hero: 'https://images.pexels.com/photos/20860588/pexels-photo-20860588.jpeg?auto=compress&w=1600',
    body: [
      'Our physiotherapists use hands-on treatment, guided movement, and clear education to help you move better and hurt less. Every plan starts with a thorough assessment so we understand your history, goals, and the demands of your daily life.',
      'From acute injuries to long-standing pain, we focus on the root cause rather than only the symptoms. Treatments may include manual therapy, therapeutic exercise, taping, and modalities—paired with a practical home program so progress continues between visits.',
      'We are trusted by families across the community for evidence-informed care delivered with warmth and respect.',
    ],
    bullets: [
      'Comprehensive one-hour initial assessment',
      'Hands-on manual therapy and joint mobilisation',
      'Progressive exercise prescription and coaching',
      'Return-to-work and return-to-sport planning',
    ],
  },
  {
    slug: 'athletic-therapy',
    name: 'Athletic Therapy',
    icon: Bike,
    tagline: 'Sport-focused care from field to finish line.',
    short:
      'Sport-focused assessment, treatment, and return-to-activity support for active patients and athletes.',
    hero: 'https://images.unsplash.com/photo-1649751361457-01d3a696c7e6?w=1600&q=85',
    body: [
      'Athletic therapy blends orthopaedic assessment with sport performance. Whether you are training for your first 10K or recovering from an ACL reconstruction, our therapists build a plan that respects your sport and your timeline.',
      'We use taping, bracing, functional testing, and progressive loading to move you safely from injury through full return to play.',
    ],
    bullets: [
      'Concussion baseline and return-to-play testing',
      'K-tape and functional bracing',
      'Sport-specific strength and conditioning',
      'On-field and event coverage available',
    ],
  },
  {
    slug: 'massage-therapy',
    name: 'Massage Therapy',
    icon: Sparkles,
    tagline: 'Release tension. Support recovery.',
    short:
      'Hands-on treatment to reduce tension, support recovery, and improve comfort between therapy sessions.',
    hero: 'https://images.unsplash.com/photo-1519823551278-64ac92734fb1?w=1600&q=85',
    body: [
      'Registered massage therapy pairs beautifully with physiotherapy. Our therapists use deep tissue, myofascial release, and Swedish techniques tailored to your goals—whether that is stress relief, recovery after training, or persistent muscular tension.',
      'Direct billing may be available when supported by your extended-health plan.',
    ],
    bullets: [
      'Deep tissue and therapeutic massage',
      'Prenatal and postnatal care',
      'Myofascial release and trigger point therapy',
      '45, 60 and 90 minute sessions',
    ],
  },
  {
    slug: 'acupuncture-dry-needling',
    name: 'Acupuncture & Dry Needling',
    icon: Zap,
    tagline: 'Targeted relief for stubborn pain.',
    short:
      'Traditional acupuncture and modern dry needling options to support pain relief and muscle function.',
    hero: 'https://images.pexels.com/photos/8313182/pexels-photo-8313182.jpeg?auto=compress&w=1600',
    body: [
      'Fine, sterile needles are placed at specific trigger points or acupuncture channels to calm the nervous system, improve circulation, and release stubborn muscle guarding. Sessions are gentle, evidence-informed, and always paired with movement therapy for lasting results.',
    ],
    bullets: [
      'Certified acupuncture providers',
      'Intramuscular Stimulation (IMS) available',
      'Great adjunct for headaches, TMJ, and chronic pain',
      'May be covered by some extended-health plans',
    ],
  },
  {
    slug: 'occupational-therapy',
    name: 'Occupational Therapy',
    icon: Shield,
    tagline: 'Live and work with less strain.',
    short:
      'Functional support for workplace injuries, daily activities, independence, and safe return to work.',
    hero: 'https://images.unsplash.com/photo-1688578735427-994ecdea3ea4?w=1600&q=85',
    body: [
      'Our occupational therapists help clients recover after workplace or motor vehicle injuries, adapt to changes in ability, and safely return to the tasks they love. We collaborate with employers and insurers to keep progress transparent.',
    ],
    bullets: [
      'Functional Capacity Evaluations',
      'Return-to-work programming',
      'Home modification assessments',
      'Cognitive and mental-health rehab',
    ],
  },
  {
    slug: 'ergonomics',
    name: 'Ergonomics',
    icon: Monitor,
    tagline: 'A workstation that works for you.',
    short:
      'Workstation and movement guidance that reduces strain and improves day-to-day comfort.',
    hero: 'https://images.unsplash.com/photo-1681418659069-eef28d44aeab?w=1600&q=85',
    body: [
      'From remote-work setups to industrial workstations, our ergonomic assessments identify the small changes that make a big difference for your neck, shoulders, and low back.',
    ],
    bullets: [
      'On-site or virtual ergonomic assessment',
      'Written recommendations and equipment sourcing',
      'Micro-break and movement coaching',
      'Corporate wellness partnerships',
    ],
  },
  {
    slug: 'sports-specific-training',
    name: 'Sports-Specific Training',
    icon: Dumbbell,
    tagline: 'Train the way your sport demands.',
    short:
      'Training plans that build strength, control, and sport-ready movement patterns.',
    hero: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=1600&q=85',
    body: [
      'Bridge the gap between rehab and full performance. Programs are periodised, tested, and tracked—so you know exactly where you stand and where you are heading.',
    ],
    bullets: [
      'One-on-one strength coaching',
      'Speed, agility and plyometric progressions',
      'Video movement analysis',
      'Small-group return-to-sport classes',
    ],
  },
  {
    slug: 'post-surgical-rehab',
    name: 'Post-Surgical Rehab',
    icon: HeartPulse,
    tagline: 'A clear roadmap back to full function.',
    short:
      'Structured recovery after ACL, rotator cuff, joint replacement and more.',
    hero: 'https://images.unsplash.com/photo-1609113160023-4e31f3765fd7?w=1600&q=85',
    body: [
      'Following your surgeon\u2019s protocol, we phase your rehab through range-of-motion, strength, and sport-specific loading. Progress is measured, milestones are celebrated, and setbacks are addressed early.',
    ],
    bullets: [
      'ACL, meniscus and knee replacement rehab',
      'Rotator cuff and shoulder reconstruction',
      'Spinal surgery recovery',
      'Return-to-sport testing',
    ],
  },
  {
    slug: 'manual-therapy',
    name: 'Manual Therapy',
    icon: Bone,
    tagline: 'Skilled hands, measurable results.',
    short:
      'Advanced joint mobilisations, soft-tissue release and neural mobilisation techniques.',
    hero: 'https://images.unsplash.com/photo-1745327883508-b6cd32e5dde5?w=1600&q=85',
    body: [
      'Manual therapy is the foundation of great physiotherapy. Our clinicians pursue advanced certifications so you receive precise, thoughtful hands-on care.',
    ],
    bullets: [
      'CAMPT-trained clinicians',
      'Joint mobilisation and manipulation',
      'Myofascial and neural techniques',
      'Paired with active exercise for lasting change',
    ],
  },
  {
    slug: 'pelvic-health',
    name: 'Pelvic Health',
    icon: Baby,
    tagline: 'Compassionate, private, expert care.',
    short:
      'Support for incontinence, prenatal, postnatal, and pelvic-pain concerns.',
    hero: 'https://images.unsplash.com/photo-1591258370814-01609b341790?w=1600&q=85',
    body: [
      'Pelvic health issues are common and highly treatable. Our pelvic-floor physiotherapists provide a private, respectful environment where you can talk openly and progress at a pace that feels right for you.',
    ],
    bullets: [
      'Prenatal preparation and postnatal recovery',
      'Bladder and bowel dysfunction',
      'Pelvic pain and Diastasis Recti',
      'Return-to-running for new parents',
    ],
  },
  {
    slug: 'vestibular-rehab',
    name: 'Vestibular Rehab',
    icon: Wind,
    tagline: 'Steadier days, calmer nights.',
    short:
      'Care for dizziness, vertigo, BPPV, and imbalance.',
    hero: 'https://images.unsplash.com/photo-1706353399656-210cca727a33?w=1600&q=85',
    body: [
      'Vestibular disorders can be disorienting and isolating. Our therapists use focused assessments and repositioning techniques to help you find stable ground again.',
    ],
    bullets: [
      'BPPV Epley and Semont repositioning',
      'Gaze stabilisation exercises',
      'Balance and fall-prevention programs',
      'Post-concussion dizziness care',
    ],
  },
  {
    slug: 'concussion-rehab',
    name: 'Concussion Rehab',
    icon: Brain,
    tagline: 'Recover on the right timeline.',
    short:
      'Multi-domain care for lingering post-concussion symptoms.',
    hero: 'https://images.unsplash.com/photo-1747239069226-55382c570116?w=1600&q=85',
    body: [
      'Recovery from concussion rarely looks like a straight line. Our team addresses neck, vestibular, ocular, and autonomic contributors so you can return to school, work, and sport with confidence.',
    ],
    bullets: [
      'Buffalo Concussion Treadmill Testing',
      'Cervical and vestibular treatment',
      'Return-to-learn and return-to-play plans',
      'Family and coach education',
    ],
  },
  {
    slug: 'ultrasound-therapy',
    name: 'Ultrasound Therapy',
    icon: Waves,
    tagline: 'Modalities that support—not replace—active care.',
    short:
      'Adjunct modalities to reduce pain and support tissue healing.',
    hero: 'https://images.pexels.com/photos/7789616/pexels-photo-7789616.jpeg?auto=compress&w=1600',
    body: [
      'When indicated, ultrasound, TENS, and shockwave can accelerate recovery. We use these tools thoughtfully, always in service of your movement goals.',
    ],
    bullets: [
      'Therapeutic ultrasound',
      'Interferential current and TENS',
      'Shockwave therapy',
      'Laser therapy',
    ],
  },
  {
    slug: 'chronic-pain',
    name: 'Chronic Pain Management',
    icon: Flame,
    tagline: 'A team that listens before it treats.',
    short:
      'A caring, science-informed approach to persistent pain.',
    hero: 'https://images.pexels.com/photos/339619/pexels-photo-339619.jpeg?auto=compress&w=1600',
    body: [
      'Chronic pain is complex. We blend education, graded exposure, movement, and mind-body strategies to help you take back control of your day.',
    ],
    bullets: [
      'Pain neuroscience education',
      'Graded movement and pacing plans',
      'Sleep and stress support',
      'Collaboration with your physician',
    ],
  },
];

export const quickActions = [
  { label: 'Request Appointment', icon: 'CalendarDays' },
  { label: 'Free Injury Screen', icon: 'MessageSquareText' },
  { label: 'Call Front Desk', icon: 'HeadphonesIcon' },
  { label: 'Find A Clinic', icon: 'MapPin' },
];

export const patientStories = [
  {
    id: 1,
    slug: 'richard-post-op-knee',
    title: "Richard\u2019s Return to Cycling After Knee Surgery",
    excerpt:
      'Six months after a total knee replacement, Richard is riding again along the lakefront.',
    image:
      'https://images.unsplash.com/photo-1609113160023-4e31f3765fd7?w=1400&q=85',
  },
  {
    id: 2,
    slug: 'ella-vestibular',
    title: "Ella\u2019s Recovery from Vertigo and Dizziness",
    excerpt:
      'A structured vestibular program helped Ella get back behind the wheel and back to work.',
    image:
      'https://images.unsplash.com/photo-1706353399656-210cca727a33?w=1400&q=85',
  },
  {
    id: 3,
    slug: 'sofia-postnatal',
    title: "Sofia\u2019s Postnatal Return-to-Running Journey",
    excerpt:
      'A gentle pelvic-health program brought Sofia back to her weekly park runs.',
    image:
      'https://images.unsplash.com/photo-1591258370814-01609b341790?w=1400&q=85',
  },
];

export const locations = [
  {
    slug: 'windsor-tecumseh-road',
    id: 1,
    name: 'Uptown Physiotherapy Clinic',
    address: '960 Tecumseh Rd E, Windsor, ON N8X 2S6',
    phone: '+1-519-252-2752',
    email: 'info@uptownphysiotherapyclinic.ca',
    hours: 'Mon\u2013Fri 8:00\u201317:00',
    image:
      'https://images.pexels.com/photos/7789616/pexels-photo-7789616.jpeg?auto=compress&w=1400',
    features: ['Free dedicated parking', '7,000 sq ft therapy space', 'Direct billing support'],
  },
];

export const team = [
  {
    slug: 'elias-jones',
    name: 'Elias Paul Cecil Jones',
    role: 'Registered Physiotherapist',
    focus: ['Sports Rehab', 'Manual Therapy'],
    photo: '/team/elias-jones.png',
    photoPosition: 'center top',
    bio:
      'Elias brings a decade of clinical experience with a focus on high-performance sport, manual therapy, and post-surgical knee rehabilitation. He has worked with regional soccer clubs and university athletics.',
    education: ['MSc Physiotherapy, McMaster University', 'CAMPT-certified'],
  },
  {
    slug: 'jenita-princess',
    name: 'Jenita Princess',
    role: 'Registered Physiotherapist',
    focus: ['Pelvic Health', 'Prenatal & Postnatal'],
    photo: '/team/jenita-princess.png',
    bio:
      'Jenita is passionate about supporting women through pregnancy, postpartum, and pelvic-pain recovery. She creates a private, unhurried space where patients can feel truly heard.',
    education: ['DPT, University of Toronto', 'Advanced Pelvic Floor Rostered'],
  },
  {
    slug: 'eric-sauerwein',
    name: 'Eric Sauerwein',
    role: 'Certified Athletic Therapist',
    focus: ['Concussion', 'Return-to-Sport'],
    photo: '/team/eric-sauerwein.png',
    bio:
      'Eric works with athletes of every level, from youth sport to national team competitors. His approach balances tissue healing with objective performance testing.',
    education: ['CAT(C), Certified Athletic Therapist', 'Buffalo Treadmill Certified'],
  },
  {
    slug: 'vaishnavi-sattigeri',
    name: 'Vaishnavi Sattigeri',
    role: 'Registered Physiotherapist',
    focus: ['Vestibular Rehab', 'Chronic Pain'],
    photo: '/team/vaishnavi-sattigeri.png',
    photoPosition: 'center top',
    photoScale: 1.24,
    bio:
      'Vaishnavi combines gentle vestibular care with modern pain-science education. Her patients describe her care as calming, thorough, and practical.',
    education: ['MPT, Manipal University', 'Vestibular Rehab Competency'],
  },
  {
    slug: 'viditi-chokshi',
    name: 'Viditi Chokshi',
    role: 'Physiotherapy Assistant',
    focus: ['Exercise Coaching', 'Post-Op Rehab'],
    photo: '/team/viditi-chokshi.png',
    bio:
      'Viditi runs our in-clinic exercise programs, ensuring every patient understands the "why" behind their home exercise plan.',
    education: ['Kinesiology Diploma', 'Certified PTA'],
  },
  {
    slug: 'palak-patel',
    name: 'Palak Patel',
    role: 'Physiotherapy Assistant',
    focus: ['Modalities', 'Manual Therapy Support'],
    photo: '/team/palak-patel.png',
    bio:
      'Palak supports our physiotherapists in delivering hands-on care and modality treatments across every discipline in the clinic.',
    education: ['BSc Health Sciences', 'PTA Certified'],
  },
  {
    slug: 'jolanda-wolda',
    name: 'Jolanda Wolda',
    role: 'PTA / Office Administration',
    focus: ['Patient Coordination', 'Billing'],
    photo: '/team/jolanda-wolda.png',
    bio:
      'Jolanda is often the first friendly voice patients hear when they call. She keeps the front-of-house running smoothly and warmly.',
    education: ['Office Administration Diploma'],
  },
  {
    slug: 'veronica-adams',
    name: 'Veronica Adams',
    role: 'Office Manager',
    focus: ['Operations', 'Insurance Support'],
    photo: '/team/veronica-adams.png',
    bio:
      'Veronica leads our administrative team and helps patients navigate insurance, WSIB, and MVA claims with clarity and care.',
    education: ['Health Administration'],
  },
];

export const news = [
  {
    id: 1,
    slug: 'when-to-see-a-physiotherapist-for-pain',
    tag: 'Physiotherapy',
    date: 'Jan 8, 2026',
    isoDate: '2026-01-08',
    title: 'When Should You See a Physiotherapist for Pain?',
    excerpt: 'Learn the signs that pain needs a physiotherapy assessment and how early care can prevent small problems from becoming larger ones.',
    image: 'https://images.pexels.com/photos/20860588/pexels-photo-20860588.jpeg?auto=compress&w=1400',
    body: [
      'Pain that lingers, keeps returning, or changes how you move is a good reason to book a physiotherapy assessment. You do not need to wait until pain is severe before asking for help.',
      'A physiotherapist looks at strength, mobility, balance, posture, daily demands, and injury history. The goal is to understand why the pain is happening and what can be changed safely.',
      'Early care often means fewer compensations, clearer exercises, and a faster return to work, sport, and normal daily routines.',
    ],
  },
  {
    id: 2,
    slug: 'what-happens-during-first-physiotherapy-visit',
    tag: 'Physiotherapy',
    date: 'Jan 12, 2026',
    isoDate: '2026-01-12',
    title: 'What Happens During Your First Physiotherapy Visit?',
    excerpt: 'A simple guide to assessment, treatment planning, and what to bring to your first appointment.',
    image: 'https://images.pexels.com/photos/20860588/pexels-photo-20860588.jpeg?auto=compress&w=1400',
    body: [
      'Your first visit usually begins with a conversation about your symptoms, goals, work, hobbies, health history, and what makes the problem better or worse.',
      'Your physiotherapist may assess range of motion, strength, joint movement, balance, walking, or sport-specific movements depending on the concern.',
      'You should leave with a clear explanation, an initial treatment plan, and practical steps you can begin at home.',
    ],
  },
  {
    id: 3,
    slug: 'sports-injury-rehab-return-to-play',
    tag: 'Sports Rehab',
    date: 'Jan 16, 2026',
    isoDate: '2026-01-16',
    title: 'Sports Injury Rehab: How to Return to Play Safely',
    excerpt: 'Returning too soon can restart the injury cycle. Here is how structured rehab helps athletes progress with confidence.',
    image: 'https://images.unsplash.com/photo-1649751361457-01d3a696c7e6?w=1400&q=85',
    body: [
      'Sports injury rehab is more than resting until symptoms calm down. It should rebuild strength, mobility, control, speed, and confidence in the movements your sport requires.',
      'A safe return-to-play plan usually moves through stages: pain control, mobility, progressive strengthening, sport-specific drills, and load testing.',
      'Objective milestones help athletes avoid guessing. Your physiotherapist or athletic therapist can guide when to progress and when to slow down.',
    ],
  },
  {
    id: 4,
    slug: 'post-surgical-rehab-why-follow-a-plan',
    tag: 'Post-Surgical Rehab',
    date: 'Jan 21, 2026',
    isoDate: '2026-01-21',
    title: 'Why Post-Surgical Rehab Needs a Clear Plan',
    excerpt: 'After surgery, the right exercises at the right time can protect healing tissue and restore function.',
    image: 'https://images.unsplash.com/photo-1609113160023-4e31f3765fd7?w=1400&q=85',
    body: [
      'Post-surgical recovery has phases. Early rehab may focus on swelling, comfort, gentle range of motion, and safe daily movement.',
      'As healing progresses, rehab usually adds strength, balance, endurance, and task-specific training. The exact pace depends on the procedure and surgeon guidance.',
      'A clear plan helps you know what is normal, what needs attention, and how to rebuild function without overloading healing tissue.',
    ],
  },
  {
    id: 5,
    slug: 'manual-therapy-and-exercise-work-together',
    tag: 'Manual Therapy',
    date: 'Jan 26, 2026',
    isoDate: '2026-01-26',
    title: 'Manual Therapy and Exercise: Why They Work Better Together',
    excerpt: 'Hands-on treatment can reduce stiffness and pain, while exercise helps make the progress last.',
    image: 'https://images.unsplash.com/photo-1745327883508-b6cd32e5dde5?w=1400&q=85',
    body: [
      'Manual therapy may include joint mobilization, soft-tissue techniques, stretching, or guided movement. It can help improve comfort and restore motion.',
      'Exercise helps your body keep those gains by building strength, coordination, and tolerance for daily demands.',
      'The best plans often combine both: hands-on care to open a window of movement, and active rehab to turn that window into lasting change.',
    ],
  },
  {
    id: 6,
    slug: 'massage-therapy-for-recovery-and-stress',
    tag: 'Massage Therapy',
    date: 'Feb 2, 2026',
    isoDate: '2026-02-02',
    title: 'Massage Therapy for Recovery, Tension, and Stress',
    excerpt: 'Massage therapy can support muscle recovery, reduce tension, and complement physiotherapy care.',
    image: 'https://images.unsplash.com/photo-1519823551278-64ac92734fb1?w=1400&q=85',
    body: [
      'Massage therapy can help with muscle tension, soreness, stress, and recovery between training or rehab sessions.',
      'Treatment may include deep tissue work, Swedish massage, trigger point techniques, or gentle approaches depending on your comfort and goals.',
      'When combined with physiotherapy, massage can make movement feel easier while your exercise plan addresses the underlying cause.',
    ],
  },
  {
    id: 7,
    slug: 'pelvic-health-physiotherapy-common-concerns',
    tag: 'Pelvic Health',
    date: 'Feb 6, 2026',
    isoDate: '2026-02-06',
    title: 'Pelvic Health Physiotherapy: Common Concerns It Can Help',
    excerpt: 'Pelvic health care can support bladder issues, pelvic pain, pregnancy, postpartum recovery, and return to activity.',
    image: 'https://images.unsplash.com/photo-1591258370814-01609b341790?w=1400&q=85',
    body: [
      'Pelvic health physiotherapy can help with leakage, urgency, pelvic pain, heaviness, pregnancy-related symptoms, and postpartum recovery.',
      'Care begins with a respectful conversation and an assessment based on your comfort level. Treatment may include education, breathing, mobility, strengthening, and pelvic floor training.',
      'These concerns are common, but they are not something you simply have to live with. A tailored plan can help you regain confidence.',
    ],
  },
  {
    id: 8,
    slug: 'vestibular-rehab-for-dizziness-and-vertigo',
    tag: 'Vestibular Rehab',
    date: 'Feb 10, 2026',
    isoDate: '2026-02-10',
    title: 'Vestibular Rehab for Dizziness and Vertigo',
    excerpt: 'Dizziness can come from different causes. Vestibular rehab identifies triggers and builds a plan for steadier movement.',
    image: 'https://images.unsplash.com/photo-1706353399656-210cca727a33?w=1400&q=85',
    body: [
      'Vestibular symptoms may include spinning, imbalance, motion sensitivity, nausea, or feeling unsteady in busy environments.',
      'A vestibular assessment looks at eye movements, balance, neck contribution, positional triggers, and how symptoms respond to movement.',
      'Treatment may include repositioning maneuvers, gaze stabilization, balance training, and gradual exposure to movements that currently feel difficult.',
    ],
  },
  {
    id: 9,
    slug: 'concussion-rehab-symptoms-that-linger',
    tag: 'Concussion Rehab',
    date: 'Feb 14, 2026',
    isoDate: '2026-02-14',
    title: 'Concussion Rehab: What to Do When Symptoms Linger',
    excerpt: 'Headache, dizziness, light sensitivity, and fatigue can improve with the right assessment and gradual rehab plan.',
    image: 'https://images.unsplash.com/photo-1747239069226-55382c570116?w=1400&q=85',
    body: [
      'Concussion recovery is not always just rest. Lingering symptoms may involve the neck, vestibular system, vision, exertion tolerance, sleep, or stress response.',
      'A rehab plan can identify what is driving symptoms and build tolerance gradually without overwhelming the nervous system.',
      'The right support can help with return to work, school, driving, sport, and daily routines.',
    ],
  },
  {
    id: 10,
    slug: 'occupational-therapy-for-return-to-work',
    tag: 'Occupational Therapy',
    date: 'Feb 18, 2026',
    isoDate: '2026-02-18',
    title: 'Occupational Therapy and Returning to Work After Injury',
    excerpt: 'Occupational therapy helps connect recovery goals with the real tasks required at work and home.',
    image: 'https://images.unsplash.com/photo-1688578735427-994ecdea3ea4?w=1400&q=85',
    body: [
      'Returning to work after injury can be stressful when symptoms, job demands, and paperwork all overlap.',
      'Occupational therapy can help assess functional abilities, plan gradual return-to-work duties, recommend modifications, and coordinate with stakeholders when needed.',
      'The goal is practical: support recovery while helping you participate safely in the tasks that matter.',
    ],
  },
  {
    id: 11,
    slug: 'ergonomics-for-neck-and-back-pain',
    tag: 'Ergonomics',
    date: 'Feb 23, 2026',
    isoDate: '2026-02-23',
    title: 'Ergonomics for Neck and Back Pain at Work',
    excerpt: 'Small workstation changes can reduce strain, but the best ergonomic plans also include movement habits.',
    image: 'https://images.unsplash.com/photo-1681418659069-eef28d44aeab?w=1400&q=85',
    body: [
      'Neck and back pain at work often comes from a mix of posture, repetition, stress, screen height, chair setup, and long periods without movement.',
      'Ergonomic care can help adjust your workspace and teach simple strategies for changing position throughout the day.',
      'The best setup is not one perfect posture. It is a setup that makes movement easier and reduces repeated strain.',
    ],
  },
  {
    id: 12,
    slug: 'ultrasound-therapy-in-rehab',
    tag: 'Ultrasound Therapy',
    date: 'Feb 27, 2026',
    isoDate: '2026-02-27',
    title: 'Where Ultrasound Therapy Fits in a Rehab Plan',
    excerpt: 'Therapeutic ultrasound may support comfort and tissue healing when used as part of an active treatment plan.',
    image: 'https://images.pexels.com/photos/7789616/pexels-photo-7789616.jpeg?auto=compress&w=1400',
    body: [
      'Ultrasound therapy is a modality sometimes used to support tissue healing, circulation, and symptom relief.',
      'It is usually most helpful when paired with education, movement, strengthening, and changes to the activities that aggravate symptoms.',
      'Your physiotherapist can explain why a modality is being used and how it fits into the bigger recovery plan.',
    ],
  },
  {
    id: 13,
    slug: 'chronic-pain-management-with-physiotherapy',
    tag: 'Chronic Pain',
    date: 'Mar 3, 2026',
    isoDate: '2026-03-03',
    title: 'Chronic Pain Management with Physiotherapy',
    excerpt: 'Persistent pain needs a plan that respects the nervous system, builds confidence, and progresses gradually.',
    image: 'https://images.pexels.com/photos/339619/pexels-photo-339619.jpeg?auto=compress&w=1400',
    body: [
      'Chronic pain is real, complex, and influenced by many factors including tissue sensitivity, stress, sleep, previous injury, and nervous system protection.',
      'Physiotherapy can help through education, pacing, graded movement, strength training, and strategies that reduce fear around activity.',
      'Progress may be gradual, but meaningful gains are possible when the plan is clear, consistent, and tailored to your life.',
    ],
  },
  {
    id: 14,
    slug: 'acupuncture-and-dry-needling-for-muscle-pain',
    tag: 'Acupuncture',
    date: 'Mar 7, 2026',
    isoDate: '2026-03-07',
    title: 'Acupuncture and Dry Needling for Muscle Pain',
    excerpt: 'Needling techniques may help calm sensitive tissues and improve movement when combined with rehab.',
    image: 'https://images.pexels.com/photos/8313182/pexels-photo-8313182.jpeg?auto=compress&w=1400',
    body: [
      'Acupuncture and dry needling use fine needles, but the assessment and treatment goals can differ depending on your condition.',
      'These techniques may help reduce pain, muscle guarding, and sensitivity so movement and exercise feel more manageable.',
      'Needling is typically one part of a plan, not the whole plan. Lasting improvement usually depends on movement, strength, and load management too.',
    ],
  },
  {
    id: 15,
    slug: 'sports-specific-training-after-rehab',
    tag: 'Sports Training',
    date: 'Mar 11, 2026',
    isoDate: '2026-03-11',
    title: 'Why Sports-Specific Training Matters After Rehab',
    excerpt: 'Getting out of pain is step one. Sports-specific training prepares your body for the speed and demands of your activity.',
    image: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=1400&q=85',
    body: [
      'A runner, hockey player, dancer, and weekend golfer all need different movement qualities. Sports-specific training bridges the gap between rehab and performance.',
      'This stage may include strength, plyometrics, agility, balance, conditioning, and sport-specific drills.',
      'The goal is not only to return, but to return with better control, confidence, and resilience.',
    ],
  },
  {
    id: 16,
    slug: 'work-related-injury-rehab-and-wsib',
    tag: 'Work Injury Rehab',
    date: 'Mar 16, 2026',
    isoDate: '2026-03-16',
    title: 'Work-Related Injury Rehab: Getting Back Safely',
    excerpt: 'A work injury plan should address symptoms, job demands, pacing, and safe return-to-work progressions.',
    image: 'https://images.unsplash.com/photo-1688578735427-994ecdea3ea4?w=1400&q=85',
    body: [
      'Work-related injuries can affect your body, routine, income, and confidence. Rehab should be practical and connected to the tasks your job requires.',
      'Treatment may include pain management, strengthening, mobility, work simulation, education, and communication about safe restrictions when appropriate.',
      'A gradual plan helps reduce flare-ups and gives you clear steps for returning to duties safely.',
    ],
  },
  {
    id: 17,
    slug: 'mva-physiotherapy-after-car-accident',
    tag: 'Motor Vehicle Accident',
    date: 'Mar 20, 2026',
    isoDate: '2026-03-20',
    title: 'Physiotherapy After a Motor Vehicle Accident',
    excerpt: 'After a car accident, physiotherapy can help with pain, stiffness, headaches, and confidence returning to activity.',
    image: 'https://images.pexels.com/photos/20860588/pexels-photo-20860588.jpeg?auto=compress&w=1400',
    body: [
      'Motor vehicle accidents can lead to neck pain, back pain, headaches, shoulder pain, dizziness, and general stiffness.',
      'A physiotherapy assessment helps identify what movements are limited, what symptoms need monitoring, and how to begin recovery safely.',
      'Treatment may include education, manual therapy, gentle mobility, strengthening, and progressive return to driving, work, and normal activity.',
    ],
  },
  {
    id: 18,
    slug: 'neck-and-back-pain-when-to-get-help',
    tag: 'Neck & Back Pain',
    date: 'Mar 24, 2026',
    isoDate: '2026-03-24',
    title: 'Neck and Back Pain: When to Get Help',
    excerpt: 'Most people experience spine pain at some point. Physiotherapy can help when it persists, spreads, or limits daily life.',
    image: 'https://images.unsplash.com/photo-1745327883508-b6cd32e5dde5?w=1400&q=85',
    body: [
      'Neck and back pain can come from joints, muscles, nerves, discs, posture, load, stress, sleep, or a combination of factors.',
      'You should consider assessment if symptoms persist, keep recurring, affect work or sleep, or travel into the arm or leg.',
      'Physiotherapy can help you understand the cause, calm symptoms, rebuild movement, and reduce the chance of repeated flare-ups.',
    ],
  },
  {
    id: 19,
    slug: 'tmj-physiotherapy-for-jaw-pain',
    tag: 'TMJ Care',
    date: 'Mar 28, 2026',
    isoDate: '2026-03-28',
    title: 'TMJ Physiotherapy for Jaw Pain and Headaches',
    excerpt: 'Jaw pain, clicking, and headaches may respond to physiotherapy that addresses the jaw, neck, and daily habits.',
    image: 'https://images.pexels.com/photos/8313182/pexels-photo-8313182.jpeg?auto=compress&w=1400',
    body: [
      'TMJ concerns can include jaw pain, clicking, locking, headaches, facial tension, and difficulty chewing.',
      'Physiotherapy may assess jaw movement, neck mobility, muscle tenderness, posture, breathing, and habits such as clenching.',
      'Treatment can include education, manual techniques, mobility exercises, relaxation strategies, and coordination with dental care when needed.',
    ],
  },
  {
    id: 20,
    slug: 'direct-billing-and-extended-health-physiotherapy',
    tag: 'Patient Guide',
    date: 'Apr 2, 2026',
    isoDate: '2026-04-02',
    title: 'Direct Billing and Extended Health Benefits for Physiotherapy',
    excerpt: 'Understanding insurance and direct billing can make it easier to start care without added stress.',
    image: 'https://images.pexels.com/photos/7789616/pexels-photo-7789616.jpeg?auto=compress&w=1400',
    body: [
      'Many extended health plans include physiotherapy, massage therapy, occupational therapy, or related rehabilitation services.',
      'Direct billing may reduce out-of-pocket paperwork by sending claims directly to your insurer when your plan supports it.',
      'Before your visit, it helps to check your annual maximums, referral requirements, deductible, and whether your insurer requires specific provider details.',
    ],
  },
];
export const testimonials = [
  {
    id: 1,
    name: 'Sanim Mathew',
    rating: 5,
    text:
      'Staff here are amazing, friendly, and helpful. I refer all my family and friends here first.',
  },
  {
    id: 2,
    name: 'Rachel B.',
    rating: 5,
    text:
      'A dedicated, caring, professional team. They are committed to addressing your concerns.',
  },
  {
    id: 3,
    name: 'Harper Lee',
    rating: 5,
    text:
      'The staff is caring and skilled, and I have seen great results from their treatment.',
  },
  {
    id: 4,
    name: 'David Kim',
    rating: 5,
    text:
      'Six weeks of care and my shoulder feels stronger than before the injury. Grateful for this team.',
  },
];

export const fundingStreams = [
  {
    name: 'Self Pay',
    text: 'Individuals can pay out-of-pocket for treatment.',
    icon: Activity,
  },
  {
    name: 'WSIB Coverage',
    text: 'Costs for work-related injuries may be covered with a valid WSIB claim number.',
    icon: Shield,
  },
  {
    name: 'MVA Insurance',
    text: 'Post-motor-vehicle-accident rehab may be funded through your auto insurer, depending on the claim.',
    icon: HeartPulse,
  },
  {
    name: 'Corporate Referral',
    text: 'Workplace wellness and ergonomic support can be arranged for employers.',
    icon: Monitor,
  },
  {
    name: 'Extended Health',
    text: 'Services may be covered by private extended-health insurance.',
    icon: Sparkles,
  },
  {
    name: 'Direct Billing',
    text: 'Direct billing may be available when your plan and insurer support it.',
    icon: HandHeart,
  },
];

export const heroImage =
  'https://images.pexels.com/photos/20860588/pexels-photo-20860588.jpeg?auto=compress&w=1800';
export const healingImage =
  'https://images.unsplash.com/photo-1649751361457-01d3a696c7e6?w=1400&q=85';
export const doctorImage =
  '/team/elias-jones.png';
export const lobbyImage =
  'https://images.pexels.com/photos/7789616/pexels-photo-7789616.jpeg?auto=compress&w=1800';
export const clinicInteriorImage =
  'https://images.unsplash.com/photo-1771574204208-b47e2d863bc5?w=1800&q=85';
export const teamGroupImage =
  '/team/elias-jones.png';

export const clinicPhone = '+1-519-252-2752';
export const clinicEmail = 'info@uptownphysiotherapyclinic.ca';
